import { useMemo } from "react"
import { toast } from "react-toastify"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import type { IBuy } from "~/hooks/useSupplyPlan"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import type { NoosPoBatch } from "~/utils/noosPoUtils"
import { invalidateBuyWrites, toBuyBatchesInput, useNoosBuyPoWriter } from "./useNoosBuyPoWriter"
import { initialDeselectedWarehouseIds } from "./createBuyDefaults"
import { useNoosBuySuggestion } from "./useNoosBuySuggestion"

// After the per-variant MOQ resolve, so it is what the buy writes, not the raw suggestion.
function sumBatchQuantity(batches: NoosPoBatch[]): number {
  return batches
    .flatMap((batch) => batch.groups.flatMap((group) => group.items))
    .reduce((sum, item) => sum + item.quantity, 0)
}

// Quantity at CreateBuyDialog's defaults: scoped, today, its initially ticked warehouses.
export function useNewBuyQuantity(
  variantIds: string[],
  enabled: boolean,
  defaultWarehouseId?: string,
): number | undefined {
  const warehouses = usePrimaryWarehouses()
  const { variants, buildBuyPoBatches } = useNoosBuyPoWriter(variantIds)
  const buyDate = startOfTodayUTC()
  const {
    coverUntil,
    query: { data: suggested },
  } = useNoosBuySuggestion({ variantIds, buyDate, isScoped: true, enabled })
  // Batches need the variant's SKU metadata.
  if (!suggested || variants.length === 0) return undefined
  const warehouseIds = warehouses.map((w) => w.id)
  const deselected = initialDeselectedWarehouseIds(warehouseIds, defaultWarehouseId)
  return sumBatchQuantity(
    buildBuyPoBatches({
      buyDate,
      coverUntil,
      suggestedOrders: suggested.suggestedOrders,
      warehouseIds: new Set(warehouseIds.filter((id) => !deselected.has(id))),
    }),
  )
}

// Fills variants onto a Draft buy as if there from the start (its date, window, warehouses and
// pre_draft POs); totalQuantity previews exactly what addToBuy writes.
export function useVariantBuyFill(variantIds: string[], buy: IBuy | undefined) {
  const { supplyPlan } = useNoosSupplyPlanDetailContext()
  const utils = api.useUtils()
  const warehouses = usePrimaryWarehouses()
  const addVariantToBuyMutation = api.supplyPlan.addVariantToBuy.useMutation()

  const buyInput = { buyId: buy?.id ?? 0 }
  const { data: detail } = api.supplyPlan.getBuy.useQuery(buyInput, { enabled: !!buy })
  const addableVariantIds = useMemo(() => {
    const onBuy = new Set(detail?.items.map((item) => item.variantId))
    return variantIds.filter((id) => !onBuy.has(id))
  }, [detail, variantIds])

  const { variants, buildBuyPoBatches } = useNoosBuyPoWriter(addableVariantIds)
  const buyDate = buy?.buyDate ? new Date(buy.buyDate) : startOfTodayUTC()
  const {
    coverUntil,
    input: suggestedInput,
    query: { data: suggested },
  } = useNoosBuySuggestion({
    variantIds: addableVariantIds,
    buyDate,
    isScoped: buy?.isScoped ?? false,
    excludePurchasePlanId: buy?.id,
    enabled: !!buy && !!detail,
  })

  function warehouseIdsFor(items: { warehouseId: string }[]): Set<string> {
    const onBuy = new Set(items.map((item) => item.warehouseId))
    return onBuy.size > 0 ? onBuy : new Set(warehouses.map((w) => w.id))
  }

  const totalQuantity =
    detail && suggested && variants.length > 0
      ? sumBatchQuantity(
          buildBuyPoBatches({
            buyDate,
            coverUntil,
            suggestedOrders: suggested.suggestedOrders,
            warehouseIds: warehouseIdsFor(detail.items),
          }),
        )
      : undefined

  async function addToBuy() {
    if (!buy || addableVariantIds.length === 0) return
    const [buyDetail, suggestion] = await Promise.all([
      utils.supplyPlan.getBuy.fetch(buyInput),
      utils.stockOutTimes.getClosingStockAndSuggestedOrders.fetch(suggestedInput),
    ])
    const batches = buildBuyPoBatches({
      buyDate,
      coverUntil,
      suggestedOrders: suggestion.suggestedOrders,
      warehouseIds: warehouseIdsFor(buyDetail.items),
    })
    await addVariantToBuyMutation.mutateAsync({
      buyId: buy.id,
      variantIds: addableVariantIds,
      batches: toBuyBatchesInput(batches, buyDate),
    })
    await invalidateBuyWrites(utils, { supplyPlanId: supplyPlan.id, buyId: buy.id })
    toast.success(`Added to ${buy.name}.`)
  }

  return { totalQuantity, addableVariantIds, isLoadingBuy: !!buy && !detail, addToBuy }
}
